// src/components/common/ResumeUploaderAI.jsx
import React, { useRef, useState, useCallback } from "react";

const UploadCloudIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    className="w-10 h-10 text-gray-400"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M12 16.5V9.75m0 0l-3.75 3.75M12 9.75l3.75 3.75M17.25 12c0 2.485-2.015 4.5-4.5 4.5s-4.5-2.015-4.5-4.5c0-2.485 2.015-4.5 4.5-4.5s4.5 2.015 4.5 4.5z"
    />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M19.5 12c0-5.239-4.261-9.5-9.5-9.5S.5 6.761.5 12s4.261 9.5 9.5 9.5"
      strokeDasharray="2 2"
    />
  </svg>
);

function ResumeUploaderAI({ onUpload, isUploading }) {
  const fileInputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const dragCounter = useRef(0);
  const dropZoneRef = useRef(null);

  const handleButtonClick = useCallback((e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback(
    (event) => {
      const file = event.target.files?.[0];
      if (file && onUpload) {
        validateAndUploadFile(file);
      }
      // Reset the input value to allow uploading the same file again
      if (event.target) {
        event.target.value = "";
      }
    },
    [onUpload]
  );

  const validateAndUploadFile = useCallback(
    (file) => {
      // Check if file type is valid
      const validTypes = [
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "application/vnd.ms-word", // Alternative MIME type for .doc
      ];

      const fileName = file.name.toLowerCase();
      const fileExtension = fileName.split(".").pop();
      const validExtensions = ["pdf", "doc", "docx"];

      // Check both MIME type and file extension
      const isValidType =
        validTypes.includes(file.type) ||
        validExtensions.includes(fileExtension);

      if (isValidType) {
        if (onUpload) {
          onUpload(file);
        }
      } else {
        alert("Please upload a valid resume file (PDF, DOC, or DOCX)");
      }
    },
    [onUpload]
  );

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragIn = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;

    // Check if the drag event contains files
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true);
    }
  }, []);

  const handleDragOut = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;

    if (dragCounter.current === 0) {
      setIsDragging(false);
    }
  }, []);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      dragCounter.current = 0;

      const files = e.dataTransfer.files;
      if (files && files.length > 0) {
        const file = files[0]; // Take only the first file
        validateAndUploadFile(file);
      }
    },
    [validateAndUploadFile]
  );

  // Effect to add/remove event listeners
  React.useEffect(() => {
    const div = dropZoneRef.current;
    if (!div) return;

    // Add event listeners
    div.addEventListener("dragenter", handleDragIn);
    div.addEventListener("dragleave", handleDragOut);
    div.addEventListener("dragover", handleDrag);
    div.addEventListener("drop", handleDrop);

    // Cleanup
    return () => {
      div.removeEventListener("dragenter", handleDragIn);
      div.removeEventListener("dragleave", handleDragOut);
      div.removeEventListener("dragover", handleDrag);
      div.removeEventListener("drop", handleDrop);
      // Reset counter on unmount
      dragCounter.current = 0;
    };
  }, [handleDragIn, handleDragOut, handleDrag, handleDrop]);

  const handleContainerClick = useCallback(
    (e) => {
      // Only trigger if clicking the container background, not buttons or other interactive elements
      if (
        e.target === e.currentTarget ||
        e.target.closest(".click-area") // Allow clicking on non-interactive content
      ) {
        handleButtonClick();
      }
    },
    [handleButtonClick]
  );

  if (isUploading) {
    return (
      <div className="p-6 my-6 border-2 border-dashed border-emerald-300 rounded-lg bg-emerald-50 text-center">
        <div className="flex flex-col items-center justify-center min-h-[150px]">
          <svg
            className="animate-spin h-8 w-8 text-emerald-700 mb-3"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
          <p className="text-emerald-800 font-semibold">
            Analyzing your resume with AI...
          </p>
          <p className="text-sm text-emerald-700">This may take a moment.</p>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={dropZoneRef}
      className={`p-6 my-6 border-2 border-dashed rounded-lg text-center transition-all duration-200 cursor-pointer relative ${
        isDragging
          ? "border-emerald-500 bg-emerald-100 scale-[1.01] shadow-lg"
          : "border-emerald-300 bg-emerald-50 hover:border-emerald-400 hover:bg-emerald-75"
      }`}
      onClick={handleContainerClick}
    >
      <div className="click-area pointer-events-none">
        <div className="flex justify-center mb-3">
          <UploadCloudIcon />
        </div>
        <h3 className="text-xl font-bold text-emerald-900">
          {isDragging
            ? "Drop your resume here!"
            : "Speed up your setup with AI"}
        </h3>
        <p className="text-emerald-700 my-2">
          {isDragging
            ? "Release to upload your resume"
            : "Drag & drop your resume here, or click anywhere to browse"}
        </p>
        <p className="text-sm text-emerald-600 mb-4">
          Supports PDF, DOC, DOCX files
        </p>
      </div>

      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        className="hidden"
        accept=".pdf,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        aria-label="Upload resume file"
      />

      <button
        onClick={handleButtonClick}
        className="pointer-events-auto relative z-10 mt-4 inline-flex items-center justify-center rounded-md border border-transparent bg-emerald-700 px-8 py-3 text-base font-medium text-white shadow-sm hover:bg-emerald-800 focus:outline-none focus:ring-2 focus:ring-emerald-600 focus:ring-offset-2 transition-colors duration-150"
        type="button"
      >
        Upload Resume & Fill Profile
      </button>

      {isDragging && (
        <div className="absolute inset-0 pointer-events-none flex items-center justify-center bg-emerald-100 bg-opacity-90 rounded-lg">
          <div className="text-center">
            <div className="animate-bounce mb-4">
              <svg
                className="w-16 h-16 text-emerald-600 mx-auto"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m3.75 9v6m3-3H9m1.5-12H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"
                />
              </svg>
            </div>
            <p className="text-lg font-semibold text-emerald-800">
              Drop to upload!
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default ResumeUploaderAI;
